import {
  LEADERBOARD_SIZE,
  fetchLeaderboard,
  fetchStanding,
  formatWhen,
  isConfigured,
} from '../cloud/scores.js';

/**
 * The Street 60 leaderboard overlay.
 *
 * Lives above the menu on its own layer so the game-over card never has to grow
 * to fit a table. Rows are built with `textContent`, never `innerHTML` — the
 * names on the board come from other players.
 */
export class Leaderboard {
  constructor({ onClose } = {}) {
    this.root = document.getElementById('leaderboard');
    this.rows = document.getElementById('board-rows');
    this.message = document.getElementById('board-message');
    this.stateLabel = document.getElementById('board-state');
    this.closeButton = document.getElementById('board-close');
    this.scroller = document.getElementById('board-scroller');

    this.onClose = onClose ?? null;
    this.isOpen = false;
    this._highlightId = null;
    this._standing = null;
    this._notice = '';
    this._loading = false;

    this.closeButton?.addEventListener('click', () => this.onClose?.());
    // Clicking the dimmed backdrop, but not the card itself, closes the board.
    this.root?.addEventListener('click', (event) => {
      if (event.target === this.root) this.onClose?.();
    });
  }

  open({ highlightId = null, standing = null, notice = '' } = {}) {
    this.isOpen = true;
    this._highlightId = highlightId;
    this._standing = standing;
    this._notice = notice;

    if (this.root) {
      this.root.inert = false;
      this.root.setAttribute('aria-hidden', 'false');
      this.root.classList.add('visible');
    }
    if (this.stateLabel) this.stateLabel.textContent = 'Leaderboard';

    this.closeButton?.focus({ preventScroll: true });
    void this.refresh();
  }

  close() {
    this.isOpen = false;
    if (!this.root) return;
    this.root.classList.remove('visible');
    this.root.setAttribute('aria-hidden', 'true');
    this.root.inert = true;
  }

  /** Fetch the board and paint it. Safe to call while already open. */
  async refresh() {
    if (this._loading) return;
    this._loading = true;

    this.#setMessage('');
    if (!isConfigured()) {
      this.#renderRows([]);
      this.#setMessage('The leaderboard is not configured yet.');
      this._loading = false;
      return;
    }

    // Paint a placeholder row-set immediately so opening feels instant.
    if (!this.rows?.childElementCount) this.#setMessage('Loading scores…');

    const board = await fetchLeaderboard(LEADERBOARD_SIZE);
    this._loading = false;

    if (!board.ok) {
      this.#renderRows([]);
      this.#setMessage(board.message);
      return;
    }

    this.#renderRows(board.rows);
    // A run that failed to post is more important than the usual summary line.
    this.#setMessage(this._notice || this.#summary(board.rows, this._standing));
  }

  /** Recompute the player's standing and repaint, keeping the rows on screen. */
  async updateStanding(score) {
    const standing = await fetchStanding(score);
    this._standing = standing;
    return standing;
  }

  #summary(rows, standing) {
    if (!rows.length) return 'No runs on the board yet. Be the first.';
    if (standing?.total) {
      return `You are #${standing.rank} of ${standing.total}.`;
    }
    // Count what is actually on screen, not everything the query returned.
    const shown = Math.min(rows.length, LEADERBOARD_SIZE);
    return `${shown} of the fastest wallets in Khartoum.`;
  }

  #setMessage(text) {
    if (!this.message) return;
    this.message.textContent = text;
    this.message.hidden = !text;
  }

  #renderRows(rows) {
    if (!this.rows) return;
    this.rows.textContent = '';

    // Hard cap: the board is a top ten even if a caller hands over more.
    rows.slice(0, LEADERBOARD_SIZE).forEach((row, index) => {
      const item = document.createElement('li');
      item.className = 'board-row';
      if (row.id != null && row.id === this._highlightId) {
        item.classList.add('board-row--me');
      }

      const rank = document.createElement('span');
      rank.className = 'board-rank';
      rank.textContent = String(index + 1);

      const name = document.createElement('span');
      name.className = 'board-name';
      name.textContent = row.name || 'PLAYER 1';

      const score = document.createElement('span');
      score.className = 'board-score';
      score.textContent = Number(row.score || 0).toLocaleString('en-US');

      const meta = document.createElement('span');
      meta.className = 'board-meta';
      // The browser is stored with every run but stays off the board — device
      // and time are enough to place a score.
      meta.textContent = [row.device, formatWhen(row.played_at)]
        .filter(Boolean)
        .join(' · ');
      if (row.played_at) {
        const exact = new Date(row.played_at);
        if (!Number.isNaN(exact.getTime())) meta.title = exact.toLocaleString();
      }

      item.appendChild(rank);
      item.appendChild(name);
      item.appendChild(score);
      item.appendChild(meta);
      this.rows.appendChild(item);
    });

    // A short board should not leave a tall empty frame behind.
    this.scroller?.classList.toggle(
      'board-scroller--short',
      this.rows.childElementCount < 5,
    );
  }
}
